<template>
  <div class="py-4">
      <div v-if="submitted" class="text-center py-5">
        <div class="mb-4">
          <font-awesome-icon :icon="['fas', 'check-circle']" size="4x" class="text-success" />
        </div>
        <h3 class="text-success mb-3">Quote Request Submitted!</h3>
        <p class="lead mb-4">
          We've received your request and will review your information shortly. Our team will contact you with a tailored pricing quote.
        </p>
        <button class="btn btn-outline-primary" @click="resetForm">
          Submit Another Request
        </button>
      </div>

      <form v-else @submit.prevent="submitForm">
        <!-- 1. Usage Estimates -->
        <div class="mb-5">
          <h5 class="mb-3"><b>1.</b> Usage Estimates</h5>
          <p class="text-muted small mb-4">
            Select the services you are planning to use and provide estimated volume of accounts accessed and/or payments initiated.
          </p>

          <!-- AIS Section -->
          <div class="border-primary mb-4">
            <div class="form-check mb-3">
              <input
                type="checkbox"
                class="form-check-input"
                id="needsAIS"
                v-model="form.needsAIS"
              >
              <label class="form-check-label font-weight-bold" for="needsAIS">
                Account Information Service (AIS)
              </label>
            </div>

            <div v-if="form.needsAIS" class="ml-4 mt-3">
              <label class="mb-2">
                Estimated number of accessed accounts per month <span class="text-danger">*</span>
              </label>
              <div class="form-row">
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">1st month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.aisMonth1"
                    placeholder="e.g., 1000"
                    required
                  >
                </div>
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">12th month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.aisMonth12"
                    placeholder="e.g., 5000"
                    required
                  >
                </div>
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">24th month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.aisMonth24"
                    placeholder="e.g., 10000"
                    required
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- PIS Section -->
          <div class="border-info">
            <div class="form-check mb-3">
              <input
                type="checkbox"
                class="form-check-input"
                id="needsPIS"
                v-model="form.needsPIS"
              >
              <label class="form-check-label font-weight-bold" for="needsPIS">
                Payment Initiation Service (PIS)
              </label>
              <small class="form-text text-muted d-block mt-1">
                Use of Payment Initiation Service is only available for companies authorised to operate as a Payment Initiation Service Provider.
              </small>
            </div>

            <div v-if="form.needsPIS" class="ml-4 mt-3">
              <label class="mb-2">
                Estimated number of payment initiations per month <span class="text-danger">*</span>
              </label>
              <div class="form-row">
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">1st month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.pisMonth1"
                    placeholder="e.g., 500"
                    required
                  >
                </div>
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">12th month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.pisMonth12"
                    placeholder="e.g., 2500"
                    required
                  >
                </div>
                <div class="col-md-4 mb-3">
                  <label class="small text-muted">24th month</label>
                  <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model="form.pisMonth24"
                    placeholder="e.g., 5000"
                    required
                  >
                </div>
              </div>
            </div>
          </div>

          <div v-if="!(form.needsAIS || form.needsPIS)" class="small text-danger mt-1">Please select at least one service.</div>
        </div>

        <hr class="my-5">

        <!-- 2. Countries of Operation -->
        <div class="mb-5">
          <h5 class="font-weight-bold mb-3">2. Countries of Operation</h5>
          <p class="text-muted small mb-4">
            Select the countries where you are planning to provide your application using Enable Banking API.
          </p>

          <div class="border rounded p-3 bg-light">
            <div class="row no-gutters">
              <div
                v-for="country in countriesEEA"
                :key="country.code"
                class="col-12 col-sm-6 col-lg-4 mb-2"
              >
                <div class="form-check">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="'country-' + country.code"
                    :value="country.code"
                    v-model="form.targetMarkets"
                  >
                  <label class="form-check-label small cursor-pointer" :for="'country-' + country.code">
                    {{ country.name }}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div v-if="form.targetMarkets.length === 0" class="small text-danger mt-1">
            Please select at least one country.
          </div>
        </div>

        <hr class="my-5">

        <!-- 3. Regulatory Status and Use-Case Description -->
        <div class="mb-5">
          <h5 class="font-weight-bold mb-3">3. Regulatory Status and Use-Case Description</h5>

          <p class="text-muted small mb-4">
            Indicate whether your company only needs Open Banking aggregation infrastructure and is authorised to provide account information and/or payment initiation services itself, or whether it needs to rely on an authorised provider.
          </p>
          <div class="mb-4">
            <div class="form-check mb-3 p-3 border rounded hover-bg-light cursor-pointer" :class="{'bg-light': form.regulatoryStatus === 'own-authorization'}">
              <input
                type="radio"
                id="reg-own"
                name="regulatoryStatus"
                class="form-check-input ml-0"
                value="own-authorization"
                v-model="form.regulatoryStatus"
              >
              <label class="form-check-label font-weight-medium w-100 cursor-pointer pl-4" for="reg-own">
                My company would use own authorisation to operate as an AISP and/or PISP
              </label>

              <div v-if="form.regulatoryStatus === 'own-authorization'" class="mt-4 pl-4 border-left">
                <div class="form-group">
                  <label class="small font-weight-bold">Type of authorisation <span class="text-danger">*</span></label>
                  <select class="form-control" v-model="form.typeOfAuthorisation" required>
                    <option value="" disabled>Select type of authorisation</option>
                    <option value="AISP" :disabled="form.needsPIS">Account Information Service Provider Registration</option>
                    <option value="PI">Payment Institution License</option>
                    <option value="EMI">Electronic Money Institution License</option>
                    <option value="CI">Credit Institution License</option>
                  </select>
                  <small v-if="form.needsPIS" class="form-text text-muted">
                    Account Information Service Provider Registration is not available when Payment Initiation Service is selected.
                  </small>
                </div>

                <div class="form-check mt-3">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="not-approved"
                    v-model="form.notApprovedYet"
                  >
                  <label class="form-check-label small" for="not-approved">
                    My company has not received regulatory approval yet
                  </label>
                </div>

                <div v-if="form.notApprovedYet" class="form-group mt-3">
                  <label class="small font-weight-bold">Estimation when the approval will be received <span class="text-danger">*</span></label>
                  <flat-pickr
                    v-model="form.estimatedApprovalDate"
                    :config="dateConfig"
                    class="form-control bg-white"
                    placeholder="dd.mm.yyyy"
                    required
                  />
                </div>
              </div>
            </div>

            <div class="form-check p-3 border rounded hover-bg-light cursor-pointer" :class="{'bg-light': form.regulatoryStatus === 'your-authorization', 'opacity-50': form.needsPIS}">
              <input
                type="radio"
                id="reg-your"
                name="regulatoryStatus"
                class="form-check-input ml-0"
                value="your-authorization"
                v-model="form.regulatoryStatus"
                :disabled="form.needsPIS"
              >
              <label class="form-check-label font-weight-medium w-100 cursor-pointer pl-4" for="reg-your">
                My company is not authorised to operate as an AISP and/or PISP and needs to contract with an authorised provider
              </label>
              <small v-if="form.needsPIS" class="form-text text-muted d-block pl-4">
                This option is not available when Payment Initiation Service is selected.
              </small>

              <div v-if="form.regulatoryStatus === 'your-authorization'" class="mt-4 pl-4 border-left">
                <div class="form-group mb-0">
                  <label class="small font-weight-bold">Use-Case Description <span class="text-danger">*</span></label>
                  <textarea
                    class="form-control"
                    rows="4"
                    v-model="form.useCaseDescription"
                    placeholder="Please describe your application and how it would use Enable Banking API"
                    required
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-5">

        <!-- 4. Agreement Effective Date -->
        <div class="mb-5">
          <h5 class="font-weight-bold mb-3">4. Agreement Effective Date</h5>
          <p class="text-muted small mb-4">What is the desired date for the agreement to enter into force and for live use of the service to begin?</p>
          <div class="form-group">
            <label class="small font-weight-bold">Effective Date <span class="text-danger">*</span></label>
            <div class="row">
              <div class="col-md-6">
                <flat-pickr
                  v-model="form.effectiveDate"
                  :config="dateConfig"
                  class="form-control bg-white"
                  placeholder="dd.mm.yyyy"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <hr class="my-5">

        <!-- 5. Company Information -->
        <div class="mb-5">
          <h5 class="font-weight-bold mb-3">5. Company Information</h5>
          <p class="text-muted small mb-4">
            Please provide your company details, which are necessary for risk evaluation and sanctions check. Note that unrestricted access in the production environment is available only to organisations.
          </p>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Company Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.companyName" required>
            </div>
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Country of Incorporation <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.countryOfIncorporation" placeholder="e.g., Finland" required>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Company Registration Number <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.companyRegistrationNumber" required>
            </div>
          </div>
        </div>

        <hr class="my-5">

        <!-- 6. Contact Person Details -->
        <div class="mb-5">
          <h5 class="font-weight-bold mb-3">6. Contact Person Details</h5>
          <p class="text-muted small mb-4">Please provide your details or the details of the person who shall receive the quote.</p>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Full Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.fullName" required>
            </div>
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.title" placeholder="e.g., CEO, CTO, Product Manager" required>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Phone Number <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="form.phoneNumber" placeholder="+358 40 1234567" required>
            </div>
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Email Address <span class="text-danger">*</span></label>
              <input type="email" class="form-control" v-model="form.emailAddress" placeholder="contact@example.com" required>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <div class="form-check">
            <input
              type="checkbox"
              class="form-check-input"
              id="confirmed"
              v-model="form.confirmed"
              required
            >
            <label class="form-check-label small" for="confirmed">
              I confirm that the information provided is accurate, true, and up to date, and understand that it will be stored and processed to provide a quote.
            </label>
          </div>
        </div>

        <div class="mt-5">
          <button type="submit" class="btn btn-primary" :disabled="submitting || !isFormValid">
            <span v-if="submitting" class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Submit Quote Request
          </button>
        </div>
      </form>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'QuoteForm',
  components: {
    flatPickr
  },
  data() {
    return {
      submitted: false,
      submitting: false,
      dateConfig: {
        dateFormat: 'd.m.Y',
        allowInput: true
      },
      countriesEEA: [
        { code: 'AT', name: 'Austria' },
        { code: 'BE', name: 'Belgium' },
        { code: 'BG', name: 'Bulgaria' },
        { code: 'HR', name: 'Croatia' },
        { code: 'CY', name: 'Cyprus' },
        { code: 'CZ', name: 'Czechia' },
        { code: 'DK', name: 'Denmark' },
        { code: 'EE', name: 'Estonia' },
        { code: 'FI', name: 'Finland' },
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'GR', name: 'Greece' },
        { code: 'HU', name: 'Hungary' },
        { code: 'IS', name: 'Iceland' },
        { code: 'IE', name: 'Ireland' },
        { code: 'IT', name: 'Italy' },
        { code: 'LV', name: 'Latvia' },
        { code: 'LI', name: 'Liechtenstein' },
        { code: 'LT', name: 'Lithuania' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'MT', name: 'Malta' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'NO', name: 'Norway' },
        { code: 'PL', name: 'Poland' },
        { code: 'PT', name: 'Portugal' },
        { code: 'RO', name: 'Romania' },
        { code: 'SK', name: 'Slovakia' },
        { code: 'SI', name: 'Slovenia' },
        { code: 'ES', name: 'Spain' },
        { code: 'SE', name: 'Sweden' }
      ],
      form: {
        needsAIS: false,
        aisMonth1: '',
        aisMonth12: '',
        aisMonth24: '',
        needsPIS: false,
        pisMonth1: '',
        pisMonth12: '',
        pisMonth24: '',
        targetMarkets: [],
        regulatoryStatus: 'your-authorization',
        typeOfAuthorisation: '',
        notApprovedYet: false,
        estimatedApprovalDate: null,
        useCaseDescription: '',
        effectiveDate: null,
        companyName: '',
        countryOfIncorporation: '',
        companyRegistrationNumber: '',
        fullName: '',
        title: '',
        phoneNumber: '',
        emailAddress: '',
        confirmed: false
      }
    }
  },
  computed: {
    ...mapGetters(['idToken', 'user']),
    isFormValid() {
      // 1. Usage Estimates
      if (!(this.form.needsAIS || this.form.needsPIS)) return false
      if (this.form.needsAIS) {
        if (!this.form.aisMonth1 || !this.form.aisMonth12 || !this.form.aisMonth24) return false
      }
      if (this.form.needsPIS) {
        if (!this.form.pisMonth1 || !this.form.pisMonth12 || !this.form.pisMonth24) return false
      }

      // 2. Countries of Operation
      if (this.form.targetMarkets.length === 0) return false

      // 3. Regulatory Status
      if (this.form.regulatoryStatus === 'own-authorization') {
        if (!this.form.typeOfAuthorisation) return false
        if (this.form.notApprovedYet && !this.form.estimatedApprovalDate) return false
      } else if (this.form.regulatoryStatus === 'your-authorization') {
        if (!this.form.useCaseDescription) return false
      } else {
        return false
      }

      // 4. Effective Date
      if (!this.form.effectiveDate) return false

      // 5. Company Information
      if (!this.form.companyName || !this.form.countryOfIncorporation || !this.form.companyRegistrationNumber) return false

      // 6. Contact Person Details
      if (!this.form.fullName || !this.form.title || !this.form.phoneNumber || !this.form.emailAddress) return false

      // 7. Confirmation
      if (!this.form.confirmed) return false

      return true
    }
  },
  watch: {
    'form.needsPIS'(needsPIS) {
      if (needsPIS) {
        this.form.regulatoryStatus = 'own-authorization'
        if (this.form.typeOfAuthorisation === 'AISP') {
          this.form.typeOfAuthorisation = ''
        }
      }
    }
  },
  mounted() {
    this.prefillUser()
  },
  methods: {
    prefillUser() {
      if (this.user) {
        if (this.user.displayName) {
          this.form.fullName = this.user.displayName
        }
        if (this.user.email) {
          this.form.emailAddress = this.user.email
        }
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return null
      const parts = dateStr.split('.')
      if (parts.length !== 3) return dateStr
      return `${parts[2]}-${parts[1]}-${parts[0]}`
    },
    async submitForm() {
      if (!this.isFormValid) {
        return
      }

      this.submitting = true

      try {
        const headers = {}
        if (this.idToken && this.idToken.token) {
          headers.Authorization = 'Bearer ' + this.idToken.token
        }

        const data = {
          services: {
            ais: this.form.needsAIS ? {
              estimated_volumes: [
                { month: 1, accounts: parseInt(this.form.aisMonth1) },
                { month: 12, accounts: parseInt(this.form.aisMonth12) },
                { month: 24, accounts: parseInt(this.form.aisMonth24) }
              ]
            } : null,
            pis: this.form.needsPIS ? {
              estimated_volumes: [
                { month: 1, payments: parseInt(this.form.pisMonth1) },
                { month: 12, payments: parseInt(this.form.pisMonth12) },
                { month: 24, payments: parseInt(this.form.pisMonth24) }
              ]
            } : null
          },
          countries: this.form.targetMarkets,
          authorisation: this.form.regulatoryStatus === 'own-authorization' ? {
            type: this.form.typeOfAuthorisation,
            estimated_approval: this.form.notApprovedYet ? this.formatDate(this.form.estimatedApprovalDate) : null
          } : null,
          use_case: {
            description: this.form.regulatoryStatus === 'your-authorization' ? this.form.useCaseDescription : null
          },
          agreement: {
            effective: this.formatDate(this.form.effectiveDate)
          },
          company: {
            name: this.form.companyName,
            country: this.form.countryOfIncorporation,
            business_id: this.form.companyRegistrationNumber
          },
          contact: {
            full_name: this.form.fullName,
            title: this.form.title,
            phone: this.form.phoneNumber,
            email: this.form.emailAddress
          },
          confirmed: this.form.confirmed
        }

        await axios({
          method: 'post',
          url: '/api/billing/quote-requests',
          data,
          headers
        })
        this.submitted = true
      } catch (error) {
        console.error('Error submitting quote request:', error)
        // If it's a 404, we'll just assume for now it's not implemented on backend but form works
        if (error.response && error.response.status === 404) {
          this.submitted = true
        } else {
          this.$store.dispatch('pushAlertMessage', {
            message: 'Unable to submit quote request. Please try again later.'
          })
        }
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.submitted = false
      this.form = {
        needsAIS: false,
        aisMonth1: '',
        aisMonth12: '',
        aisMonth24: '',
        needsPIS: false,
        pisMonth1: '',
        pisMonth12: '',
        pisMonth24: '',
        targetMarkets: [],
        regulatoryStatus: 'your-authorization',
        typeOfAuthorisation: '',
        notApprovedYet: false,
        estimatedApprovalDate: null,
        useCaseDescription: '',
        effectiveDate: null,
        companyName: '',
        countryOfIncorporation: '',
        companyRegistrationNumber: '',
        fullName: '',
        title: '',
        phoneNumber: '',
        emailAddress: '',
        confirmed: false
      }
      this.prefillUser()
    }
  }
}
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8f9fa;
}
.cursor-pointer {
  cursor: pointer;
}
.font-weight-medium {
  font-weight: 500;
}
.form-check-input {
  margin-top: 0.3rem;
}
</style>
